import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Build as BuildIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  PrecisionManufacturing as ProductionIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface DomainData {
  percentage: number;
  compliant: number;
  total: number;
  gaps?: string[];
  unavailable?: boolean;
}

interface ISA95Report {
  overallPercentage: number;
  generatedAt: string;
  domains: Record<string, DomainData>;
}

interface DomainMeta {
  label: string;
  icon: React.ReactElement;
}

interface DomainCardProps {
  domainKey: string;
  domain: DomainData;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DOMAIN_META: Record<string, DomainMeta> = {
  equipment: { label: 'Equipment', icon: <BuildIcon /> },
  material:  { label: 'Material',  icon: <InventoryIcon /> },
  personnel: { label: 'Personnel', icon: <PeopleIcon /> },
  production: { label: 'Production', icon: <ProductionIcon /> },
  quality:   { label: 'Quality',   icon: <VerifiedIcon /> },
};

const getComplianceColor = (pct: number): string => {
  if (pct >= 80) return '#28a745';
  if (pct >= 60) return '#ffc107';
  return '#dc3545';
};

const getComplianceSeverity = (pct: number): 'success' | 'warning' | 'error' => {
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'error';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const DomainCard: React.FC<DomainCardProps> = ({ domainKey, domain }) => {
  const [gapsOpen, setGapsOpen] = useState<boolean>(false);
  const meta: DomainMeta = DOMAIN_META[domainKey] || { label: domainKey, icon: <AssessmentIcon /> };
  const color = getComplianceColor(domain.percentage);
  const hasGaps = Array.isArray(domain.gaps) && domain.gaps.length > 0;

  return (
    <Card
      sx={{
        height: '100%',
        borderTop: `4px solid ${domain.unavailable ? '#9e9e9e' : color}`,
      }}
    >
      <CardContent>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: domain.unavailable ? 'text.disabled' : color, display: 'flex', alignItems: 'center' }}>
              {meta.icon}
            </Box>
            <Typography variant="h6" sx={{ color: domain.unavailable ? 'text.disabled' : 'text.primary' }}>
              {meta.label}
            </Typography>
          </Box>
          {domain.unavailable && (
            <Chip label="Unavailable" size="small" color="default" variant="outlined" />
          )}
        </Box>

        {domain.unavailable ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Service unavailable — compliance data could not be retrieved.
          </Alert>
        ) : (
          <>
            {/* Percentage score */}
            <Typography
              variant="h3"
              sx={{ color, fontWeight: 'bold', textAlign: 'center', my: 1 }}
            >
              {domain.percentage}%
            </Typography>

            {/* Progress bar */}
            <LinearProgress
              variant="determinate"
              value={domain.percentage}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#e0e0e0',
                mb: 1,
                '& .MuiLinearProgress-bar': { backgroundColor: color },
              }}
            />

            {/* Subtitle */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
              {domain.compliant} of {domain.total} checks compliant
            </Typography>

            {/* Gaps section */}
            {hasGaps && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() => setGapsOpen((prev) => !prev)}
                >
                  <Typography variant="body2" color="error.main" fontWeight="medium">
                    {domain.gaps!.length} gap{domain.gaps!.length !== 1 ? 's' : ''} identified
                  </Typography>
                  <IconButton size="small">
                    {gapsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={gapsOpen}>
                  <List dense disablePadding>
                    {domain.gaps!.map((gap, idx) => (
                      <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={
                            <Typography variant="caption" color="text.secondary">
                              • {gap}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}

            {!hasGaps && (
              <Chip
                label="No gaps"
                size="small"
                color="success"
                variant="outlined"
                sx={{ display: 'block', width: 'fit-content', mx: 'auto', mt: 1 }}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ISA95ComplianceReport: React.FC = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState<ISA95Report | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE}/analytics/reports/isa95-compliance`, { headers });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const data: ISA95Report = await response.json();
      setReport(data);
    } catch (err) {
      console.error('ISA-95 compliance fetch error:', err);
      setError((err as Error).message || 'Failed to load ISA-95 compliance report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const overallColor = report ? getComplianceColor(report.overallPercentage) : '#9e9e9e';
  const overallSeverity = report ? getComplianceSeverity(report.overallPercentage) : 'info';

  const domainKeys = ['equipment', 'material', 'personnel', 'production', 'quality'];

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => { e.preventDefault(); navigate('/'); }}
        >
          Home
        </Link>
        <Typography color="text.primary">ISA-95 Compliance Report</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            ISA-95 Compliance Report
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Measures conformance to ISA-95 manufacturing operations management standards across all domains.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchReport}
          disabled={loading}
          sx={{ mt: 0.5 }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      {/* Generated-at timestamp */}
      {report && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Generated at: {new Date(report.generatedAt).toLocaleString()}
        </Typography>
      )}

      {/* Loading state */}
      {loading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress size={56} />
        </Box>
      )}

      {/* Top loading bar when refreshing with existing data */}
      {loading && report && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error state */}
      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchReport}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Main content */}
      {report && (
        <>
          {/* Overall compliance score */}
          <Card sx={{ mb: 4, borderTop: `6px solid ${overallColor}` }}>
            <CardContent>
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary" display="block">
                    Overall ISA-95 Compliance
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      color: overallColor,
                      fontWeight: 'bold',
                      fontSize: { xs: '4rem', md: '6rem' },
                      lineHeight: 1.1,
                    }}
                  >
                    {report.overallPercentage}%
                  </Typography>
                  <Chip
                    label={
                      report.overallPercentage >= 80
                        ? 'Compliant'
                        : report.overallPercentage >= 60
                        ? 'Partially Compliant'
                        : 'Non-Compliant'
                    }
                    color={overallSeverity}
                    sx={{ mt: 1, fontWeight: 'bold' }}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom>
                    Domain Summary
                  </Typography>
                  {domainKeys.map((key) => {
                    const domain = report.domains[key];
                    if (!domain) return null;
                    const meta: DomainMeta = DOMAIN_META[key] || { label: key };
                    const color = domain.unavailable ? '#9e9e9e' : getComplianceColor(domain.percentage);
                    return (
                      <Box key={key} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {meta.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color }} fontWeight="bold">
                            {domain.unavailable ? 'N/A' : `${domain.percentage}%`}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={domain.unavailable ? 0 : domain.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { backgroundColor: color },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Domain cards */}
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Domain Breakdown
          </Typography>
          <Grid container spacing={3}>
            {domainKeys.map((key) => {
              const domain = report.domains[key];
              if (!domain) return null;
              return (
                <Grid item xs={12} sm={6} lg={4} key={key}>
                  <DomainCard domainKey={key} domain={domain} />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 4 }}>
        <Button variant="outlined" onClick={() => navigate('/analytics')}>
          Analytics Dashboard
        </Button>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default ISA95ComplianceReport;
